import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { ActionType, IPagination, Order } from "../common/Interfaces";
import { getComparator } from "../common/utils";

export interface ITeacher {
  id: number;
  teacherId: number;
  name: string;
  email?: string;
  classesno: number;
  studentsno: number;
}

interface ITeacherEntities {
  [key: number]: ITeacher;
}
export enum TeachersFilterTypes {
  Teacher = "Teacher",
  Email = "Email",
}
export enum TeachersColumns {
  Teacher = "Teacher",
  Email = "Email",
  ClassesNo = "ClassesNo",
  StudentsNo = "StudentsNo",
}

export enum TeachersActionLabels {
  SharePassword = "SharePassword",
  Password = "Passwrod",
  Edit = "Edit",
  Delete = "Delete",
}

interface ITeachersState {
  entities: ITeacherEntities;
  pagination: IPagination;
  actionMode: ActionType;
  filterType: TeachersFilterTypes;
  searchText: string;
  order: Order;
  orderBy: TeachersColumns;
  updateItem: ITeacher | null;
  deleteItem: ITeacher | null;
}

const initialState: ITeachersState = {
  entities: {},
  pagination: {
    currentPage: 1,
    totalPages: 1,
    pageSize: 15,
  },
  actionMode: ActionType.list,
  filterType: TeachersFilterTypes.Teacher,
  searchText: "",
  order: "asc",
  orderBy: TeachersColumns.Teacher,
  updateItem: null,
  deleteItem: null,
};
export const teachersSlice = createSlice({
  name: "teachers",
  initialState,
  reducers: {
    teachersLoaded(state: ITeachersState, action: PayloadAction<ITeacher[]>) {
      state.entities = action.payload.reduce<ITeacherEntities>(
        (entities, item) => {
          entities[item.id] = item;
          return entities;
        },
        {}
      );
      state.pagination.totalPages = Math.ceil(
        action.payload.length / state.pagination.pageSize
      );
    },
    currentPageSet(state: ITeachersState, action: PayloadAction<number>) {
      state.pagination.currentPage = action.payload;
    },
    totalPagesSet(state: ITeachersState, action: PayloadAction<number>) {
      state.pagination.totalPages = action.payload;
    },
    actionModeSet(state: ITeachersState, action: PayloadAction<ActionType>) {
      state.actionMode = action.payload;
    },
    filterSet(
      state: ITeachersState,
      action: PayloadAction<TeachersFilterTypes>
    ) {
      state.filterType = action.payload;
    },
    searchTextSet(state: ITeachersState, action: PayloadAction<string>) {
      state.searchText = action.payload;
    },
    orderSet(state: ITeachersState, action: PayloadAction<Order>) {
      state.order = action.payload;
    },
    orderBySet(state: ITeachersState, action: PayloadAction<TeachersColumns>) {
      state.orderBy = action.payload;
    },
    updateItemSet(
      state: ITeachersState,
      action: PayloadAction<ITeacher | null>
    ) {
      state.updateItem = action.payload;
    },
    deleteItemSet(
      state: ITeachersState,
      action: PayloadAction<ITeacher | null>
    ) {
      state.deleteItem = action.payload;
    },
  },
});

export const selectOrder = (state: RootState) => state.teachers.order;
export const selectOrderBy = (state: RootState) => state.teachers.orderBy;
export const selectUpdateItem = (state: RootState) => state.teachers.updateItem;
export const selectDeleteItem = (state: RootState) => state.teachers.deleteItem;
const selectTeacherEntities = (state: RootState) => state.teachers.entities;
export const selectTeachers = createSelector(
  selectTeacherEntities,
  (entities) => Object.values(entities)
);
export const selectFilterType = (state: RootState) => state.teachers.filterType;
export const selectSearchText = (state: RootState) => state.teachers.searchText;

export const selectFilteredTeachers = createSelector(
  selectTeachers,
  selectFilterType,
  selectSearchText,
  (teachers, filter, text) => {
    const list = teachers.filter((item) => {
      let searchIndex: string =
        TeachersFilterTypes[filter as keyof typeof TeachersFilterTypes];
      if (searchIndex === "Teacher") searchIndex = "name";
      const result = text
        ? item[searchIndex.toLowerCase()]
            .toLowerCase()
            .includes(text.toLowerCase())
        : true;
      return result;
    });
    return list;
  }
);
export const selectTeachersIDs = createSelector(selectTeachers, (teachers) =>
  teachers.map((item) => item.id)
);
export const selectTeacherById = (state: RootState, teacherId: number) =>
  state.teachers.entities[teacherId];

export const selectTeachersPagination = (state: RootState): IPagination =>
  state.teachers.pagination;

export const currentPageTeacherList = createSelector(
  selectFilteredTeachers,
  selectOrder,
  selectOrderBy,
  selectTeachersPagination,
  (list, order, orderBy, pagination: IPagination) => {
    const startIndex = (pagination.currentPage - 1) * pagination.pageSize;
    const endIndex = startIndex + pagination.pageSize;
    const sortedList = list
      .sort(
        getComparator<ITeacher>(
          order,
          (orderBy === "Teacher"
            ? "name"
            : orderBy.toLowerCase()) as keyof ITeacher
        )
      )
      .slice(startIndex, endIndex);
    return sortedList;
  }
);

export const selectTeachersActionMode = (state: RootState): ActionType =>
  state.teachers.actionMode;

export const {
  teachersLoaded,
  currentPageSet,
  totalPagesSet,
  actionModeSet,
  filterSet,
  searchTextSet,
  orderBySet,
  orderSet,
  updateItemSet,
  deleteItemSet,
} = teachersSlice.actions;

export default teachersSlice.reducer;
